/**
 * Audit module — read-only, append-only trail of family changes. Parents only.
 */
import { useMemo } from 'react';
import type { AuditEntry } from '@solandia/core';
import { useI18n } from '../../i18n';
import { useApi } from '../../api/ApiProvider';
import { useFamily } from '../../family/FamilyContext';
import { useAsync } from '../../hooks/useAsync';
import { makePeopleResolver } from '../../family/people';
import { Badge, EmptyState, ErrorState, SectionHeader, Spinner } from '../../components/ui';

function actionTone(action: string): 'primary' | 'success' | 'danger' | 'default' {
  if (action === 'create' || action === 'invite') return 'primary';
  if (action === 'settle') return 'success';
  if (action === 'delete' || action === 'remove') return 'danger';
  return 'default';
}

export function AuditModule() {
  const { t } = useI18n();
  const api = useApi();
  const { family, me } = useFamily();

  const { data, loading, error, reload } = useAsync<AuditEntry[]>(async () => {
    const res = await api.listAudit(family.id, { limit: 100 });
    return res.items;
  }, [family.id]);

  const people = useMemo(
    () => makePeopleResolver(me, t.expenses.you),
    [me, t.expenses.you],
  );

  const ordered = useMemo(
    () => [...(data ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    [data],
  );

  return (
    <section>
      <SectionHeader title={t.audit.title} subtitle={t.audit.lead} />

      {loading ? <Spinner /> : null}
      {error ? <ErrorState message={t.errors.generic} onRetry={reload} /> : null}
      {ordered.length === 0 && !loading ? (
        <EmptyState emoji="🧭" title={t.audit.empty} />
      ) : null}

      <ul className="list">
        {ordered.map((a) => (
          <li key={a.id} className="list-item">
            <div className="grow stack-sm">
              <div className="row wrap">
                <Badge tone={actionTone(a.action)}>{a.action}</Badge>
                <span className="text-sm muted">{a.entityType}</span>
              </div>
              <span>{a.summary}</span>
              <span className="text-xs muted">
                {people.label(a.actorId)} · {new Date(a.createdAt).toLocaleString('sk-SK')}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
